"use client";

import * as React from "react";
import { CircleCheck as CheckCircle2, ShieldCheck, Mail, Sparkles, Loader as Loader2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl border border-border/40 bg-card/60 p-6 md:p-8 text-center backdrop-blur-xs">
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-accent/15 text-accent">
          <CheckCircle2 className="size-6 text-accent" />
        </div>
        <h3 className="font-serif text-xl font-semibold text-foreground">
          You are on the list
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {successMessage || "Thank you for subscribing. Look out for your first edition this Sunday."}
        </p>
        <div className="mt-6">
          <Button type="button" variant="outline" size="sm" onClick={resetForm} className="border-border text-foreground hover:bg-accent/10 hover:text-foreground">
            Register another address
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-12 md:gap-3 items-end">
        <div className="md:col-span-5 text-left">
          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-1.5">
                <FormLabel className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                  Email Address
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                    <Input type="email" placeholder="Enter your email address" className="h-11 bg-card/80 pl-10 pr-4 text-foreground placeholder:text-muted-foreground border-border focus-visible:border-ring focus-visible:ring-ring" {...field} />
                  </div>
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>} />
        </div>

        <div className="md:col-span-4 text-left">
          <FormField control={control} name="pet_preference" rules={{
          required: "Please choose your pet preference"
        }} render={({
          field
        }) => <FormItem className="space-y-1.5">
                <FormLabel className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                  Pet Preference
                </FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-11 w-full bg-card/80 px-3.5 text-foreground border-border focus-visible:border-ring focus-visible:ring-ring">
                      <SelectValue placeholder="Select Dogs, Cats, or Both" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-popover text-popover-foreground border-border">
                    <SelectItem value="dogs">Dogs</SelectItem>
                    <SelectItem value="cats">Cats</SelectItem>
                    <SelectItem value="both">Both Dogs & Cats</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage className="text-xs" />
              </FormItem>} />
        </div>

        <div className="md:col-span-3">
          <Button type="submit" disabled={isSubmitting} className="w-full h-11 bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-colors shadow-sm">
            {isSubmitting ? <>
                <Loader2 className="size-4 animate-spin mr-2" />
                Subscribing...
              </> : "Join the Pack"}
          </Button>
        </div>
      </div>

      {submitError && <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-left text-xs text-destructive-foreground">
          {submitError}
        </div>}
    </Form>;
}
export default function Newsletter() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="newsletter" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-16 md:py-20 lg:py-24 border-t border-border/20">
      {/* Background ambient accents */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-full max-w-4xl rounded-full bg-accent/5 blur-3xl" aria-hidden="true" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-3xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={containerVariants} className="rounded-2xl border border-border/40 bg-card/40 p-6 sm:p-10 md:p-12 shadow-xl backdrop-blur-sm">
            {/* Header / Eyebrow */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/50 bg-muted/40 px-3.5 py-1 text-xs font-semibold tracking-wider uppercase text-accent mb-4">
                <Sparkles className="size-3.5 text-accent" />
                <span>Weekly Clinical Dispatch</span>
              </div>

              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-foreground text-balance">
                Better pet care, delivered weekly.
              </h2>

              <p className="mt-3.5 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
                Useful health insights, pet-care advice and product recommendations for dog and cat owners.
              </p>
            </div>

            {/* Form Section */}
            <div className="mt-8 pt-6 border-t border-border/40">
              <FormProvider formId="home_newsletter_form" sectionName="newsletter-section" defaultValues={{
              email: "",
              pet_preference: ""
            }} validationRules={{
              email: {
                required: true,
                pattern: "email"
              },
              pet_preference: {
                required: true
              }
            }} submitText="Join the Pack" successMessage="You're subscribed! We'll send the latest veterinary-reviewed guidance directly to your inbox." errorMessage="We could not process your subscription. Please check your details and try again.">
                <NewsletterForm />
              </FormProvider>
            </div>

            {/* Trust and Reassurance Line */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground/80">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-accent" />
                <span>No spam, guaranteed. Unsubscribe in one click anytime.</span>
              </div>
              <div className="hidden sm:inline-block text-border" aria-hidden="true">
                •
              </div>
              <span>Published every Sunday morning</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}